import { cn } from '../../utils/utils';

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  variant?:
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'indigo'
    | 'neutral'
    | 'lesson'
    | 'notes'
    | 'practice'
    | 'reference';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
}

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
  lg: 'px-4 py-2 text-base',
};

// Token-driven tints. Light mode uses a /10 fill with the darker *-emphasis
// text for WCAG AA; dark mode uses a /20 fill with the lighter base hue.
const variantClasses = {
  // Status colors
  success:
    'bg-success/10 text-success-emphasis dark:bg-success/20 dark:text-success',
  warning:
    'bg-warning/10 text-warning-emphasis dark:bg-warning/20 dark:text-warning',
  error:
    'bg-destructive/10 text-destructive-emphasis dark:bg-destructive/20 dark:text-destructive',
  info: 'bg-info/10 text-info-emphasis dark:bg-info/20 dark:text-info',
  // Brand indigo - matches the chat conversation selector accent.
  indigo: 'bg-indigo-50 text-indigo-700 dark:tint-brand',
  neutral: 'bg-muted text-muted-foreground',
  // Learning colors
  lesson: 'bg-lesson/10 text-lesson dark:bg-lesson/20',
  notes: 'bg-notes/10 text-notes dark:bg-notes/20',
  practice: 'bg-practice/10 text-practice dark:bg-practice/20',
  reference: 'bg-reference/10 text-reference dark:bg-reference/20',
};

export function StatusBadge({
  variant = 'info',
  size = 'sm',
  children,
  className,
  ...rest
}: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium transition-colors',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
